import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { tenantFetch } from "../lib/api-client";
import { useTenant } from "../lib/tenant-context";
import { RequirePlan } from "../components/RouteGuard";
import { Box } from "@solar-icons/react";

interface Product {
  id: string;
  sku: string | null;
  title: string;
  description: string | null;
  brand: string | null;
  category: string | null;
  price: number | null;
  currency: string;
  image_url: string | null;
  thumbnail_url: string | null;
  status: "draft" | "active" | "archived";
  embedding_status: "pending" | "processing" | "complete" | "failed";
  created_at: string;
}

interface AIEnrichment {
  vision?: {
    product_type?: string;
    brand?: string;
    color?: string;
    material?: string;
    condition?: string;
    features?: string[];
    description?: string;
  };
  value?: {
    estimated_value?: number;
    price_range?: { min: number; max: number };
    confidence?: number;
    factors?: string[];
  };
  intelligence?: {
    category?: string;
    attributes?: Record<string, string>;
    keywords?: string[];
    similar_products?: string[];
  };
}

interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const Route = createFileRoute("/products")({
  component: () => (
    <RequirePlan required="starter" featureName="Products" icon={<Box size={64} weight="LineDuotone" />}>
      <Products />
    </RequirePlan>
  ),
});

function Products() {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [embeddingFilter, setEmbeddingFilter] = useState(""); // NEW: Filter by enrichment status
  const [categories, setCategories] = useState<string[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [enrichmentResult, setEnrichmentResult] = useState<{
    product: Product;
    data: AIEnrichment;
  } | null>(null);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);

  // NEW: Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Analyze All state
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState({
    current: 0,
    total: 0,
  });

  // Warn user before navigating away during enrichment
  useEffect(() => {
    if (analyzingAll) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue =
          "Enrichment is in progress. Are you sure you want to leave?";
        return e.returnValue;
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [analyzingAll]);

  // Delete product
  const deleteProduct = async (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Delete "${product.title}"? This cannot be undone.`)) return;

    setDeletingId(product.id);
    try {
      const response = await tenantFetch(`/api/products/${product.id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setProducts((prev) => prev.filter((p) => p.id !== product.id));
        setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
      } else {
        alert("Failed to delete product");
      }
    } catch {
      alert("Failed to delete product");
    } finally {
      setDeletingId(null);
    }
  };

  // Enrich product with AI
  const enrichProduct = async (product: Product) => {
    if (!product.image_url && !product.thumbnail_url) {
      setEnrichmentError("Product needs an image for AI analysis");
      return;
    }

    setEnrichingId(product.id);
    setEnrichmentError(null);

    const enrichment: AIEnrichment = {};
    const imageUrl = product.image_url || product.thumbnail_url;

    try {
      console.log("Starting AI enrichment for:", product.title);

      // Call Vision API
      const visionRes = await fetch("/api/v1/vision/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image_url: imageUrl,
          extract_attributes: true,
          include_condition: true,
        }),
      });

      if (visionRes.ok) {
        const visionData = await visionRes.json();
        console.log("Vision API response:", visionData);
        const result = visionData.result || visionData.analysis || {};
        enrichment.vision = {
          product_type: result.identification?.type,
          brand: result.identification?.brand,
          color: result.attributes?.colors,
          material: result.attributes?.materials,
          condition: result.condition?.grade,
          features: result.features
            ? (Object.values(result.features) as string[])
            : [],
          description: result.value_estimate,
        };
      } else {
        const visionError = await visionRes.json().catch(() => ({}));
        console.error("Vision API error:", visionError);
        enrichment.vision = {
          product_type: "Analysis failed",
          description: visionError.error || "Image could not be analyzed",
        };
      }

      // Call Value API
      const valueRes = await fetch("/api/v1/value/estimate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: {
            title: product.title,
            brand: product.brand,
            category: product.category,
            description: product.description,
            original_price: product.price,
            condition: enrichment.vision?.condition || "A",
          },
          image_url: imageUrl,
          market: "uk",
        }),
      });

      if (valueRes.ok) {
        const valueData = await valueRes.json();
        console.log("Value API response:", valueData);
        enrichment.value = {
          estimated_value: valueData.estimate?.mid,
          price_range: {
            min: valueData.estimate?.low,
            max: valueData.estimate?.high,
          },
          confidence: valueData.estimate?.confidence,
          factors: valueData.ai_insights?.factors || [],
        };
      } else {
        console.error("Value API error:", await valueRes.text());
      }

      // Call Product Intelligence API
      const intelliRes = await tenantFetch("/api/v1/products/enrich", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: {
            id: product.id,
            title: product.title,
            brand: product.brand,
            description: product.description,
            category: product.category,
            image_url: imageUrl,
          },
          enrichments: ["categories", "attributes", "seo"],
        }),
      });

      if (intelliRes.ok) {
        const intelliData = await intelliRes.json();
        console.log("Intelligence API response:", intelliData);
        const enrichments = intelliData.enrichments || {};
        enrichment.intelligence = {
          category: [
            enrichments.categories?.primary_category,
            enrichments.categories?.secondary_category,
            enrichments.categories?.tertiary_category,
          ]
            .filter(Boolean)
            .join(" > "),
          attributes: {
            ...enrichments.attributes,
            tags: enrichments.categories?.tags?.join(", "),
          },
          keywords: enrichments.seo?.keywords || [],
        };
      } else {
        console.error("Intelligence API error:", await intelliRes.text());
      }

      setEnrichmentResult({ product, data: enrichment });
    } catch (err) {
      setEnrichmentError(
        err instanceof Error ? err.message : "AI enrichment failed",
      );
    } finally {
      setEnrichingId(null);
    }
  };

  // Fetch products
  useEffect(() => {
    async function fetchProducts() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.set("page", pagination.page.toString());
        params.set("limit", pagination.limit.toString());
        if (search) params.set("search", search);
        if (status) params.set("status", status);
        if (category) params.set("category", category);
        if (embeddingFilter) params.set("embedding_status", embeddingFilter);

        const response = await tenantFetch(`/api/products?${params}`);

        // Check if response is JSON
        const contentType = response.headers.get("content-type");
        if (
          !response.ok ||
          !contentType ||
          !contentType.includes("application/json")
        ) {
          throw new Error("API returned non-JSON response");
        }

        const data: ProductsResponse = await response.json();
        if (data.products && data.products.length > 0) {
          setProducts(data.products);
          setPagination(data.pagination);
        } else {
          // API returned empty, use demo data
          throw new Error("No products from API");
        }
      } catch (err) {
        // Fall back to demo data based on tenant
        console.debug("Using demo products for", currentTenant.id, err);
        const demoProducts = getTenantDemoProducts(currentTenant.id);
        if (demoProducts.length > 0) {
          setProducts(demoProducts);
          setPagination({
            page: 1,
            limit: 20,
            total: demoProducts.length,
            pages: 1,
          });
          setError(null);
        } else {
          setError(err instanceof Error ? err.message : "An error occurred");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [
    pagination.page,
    search,
    status,
    category,
    embeddingFilter,
    currentTenant.id,
  ]);

  // Fetch categories for filter
  useEffect(() => {
    async function fetchCategories() {
      try {
        const response = await tenantFetch("/api/products/categories");
        if (response.ok) {
          const data = await response.json();
          setCategories(
            data.categories.map((c: { category: string }) => c.category),
          );
        }
      } catch {
        // Silently fail - categories are optional
      }
    }
    fetchCategories();
  }, []);

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Shopify sync handler
  const handleShopifySync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);

    try {
      // Check if Shopify is connected
      const statusResponse = await tenantFetch(
        "/api/v1/connectors/shopify/status",
      );
      const statusData = statusResponse.ok ? await statusResponse.json() : null;
      const hasConnection =
        statusData?.connections?.some(
          (c: { status: string }) => c.status === "active",
        ) || false;

      if (!hasConnection) {
        // Not connected — redirect to Shopify settings
        navigate({ to: "/settings/shopify" as any });
        return;
      }

      // Connected — sync catalog
      const response = await tenantFetch(
        "/api/v1/connectors/shopify/sync-configured",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Sync failed");
      }

      setSyncResult(
        `Synced ${data.processed || data.total || 0} products from Shopify`,
      );
      // Refresh products list
      setPagination((p) => ({ ...p, page: 1 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Enrich All Products handler (fast - no vision, text-only LLM)
  const handleAnalyzeAll = async () => {
    // Get products that are not yet fully enriched (no image required!)
    const productsToEnrich = products.filter(
      (p) => p.embedding_status !== "complete",
    );

    if (productsToEnrich.length === 0) {
      setSyncResult("All products are already enriched!");
      return;
    }

    setAnalyzingAll(true);
    setAnalyzeProgress({ current: 0, total: productsToEnrich.length });
    setError(null);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < productsToEnrich.length; i++) {
      const product = productsToEnrich[i];
      setAnalyzeProgress({ current: i + 1, total: productsToEnrich.length });

      try {
        // Use fast semantic enrichment (text-only LLM, no vision)
        const response = await tenantFetch("/api/v1/products/enrich-semantic", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product: {
              id: product.id,
              title: product.title,
              brand: product.brand,
              category: product.category,
              description: product.description,
              price: product.price,
            },
          }),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          successCount++;
          console.log(
            `Enriched ${product.title} in ${result.processingTimeMs}ms`,
          );
          // Update product status in local state
          setProducts((prev) =>
            prev.map((p) =>
              p.id === product.id
                ? { ...p, embedding_status: "complete" as const }
                : p,
            ),
          );
        } else {
          failCount++;
          console.error(
            `Enrichment failed for ${product.title}:`,
            result.error,
          );
        }
      } catch (err) {
        failCount++;
        console.error(`Enrichment error for ${product.title}:`, err);
      }
    }

    setAnalyzingAll(false);
    setSyncResult(
      `Enriched ${successCount} products${failCount > 0 ? ` (${failCount} failed)` : ""}`,
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Products
          </h1>
          <p className="text-base mt-1" style={{ color: "#A9A9A9" }}>
            Manage your product catalogue
          </p>
        </div>
        <div className="flex gap-3">
          {/* Enrich All Button - Fast semantic enrichment (no vision) */}
          <button
            onClick={handleAnalyzeAll}
            disabled={analyzingAll || products.length === 0}
            className="px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            title="Generate semantic enrichment for all products (fast, no image processing)"
          >
            {analyzingAll ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Enriching {analyzeProgress.current}/{analyzeProgress.total}
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Enrich All
              </>
            )}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2"
            style={{ backgroundColor: "#171717", color: "#fff" }}
          >
            <span>+</span> Add Product
          </button>
          <Link to="/products/import" className="btn-secondary">
            Import CSV
          </Link>
          <button
            className="btn-primary"
            onClick={handleShopifySync}
            disabled={syncing}
          >
            {syncing
              ? "Syncing..."
              : currentTenant.id === "nike"
                ? "SYNC Nike.com"
                : "Sync Catalog"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search products..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 px-4 py-2 border text-sm"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(169,169,169,0.3)",
            color: "#171717",
          }}
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-4 py-2 border text-sm"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(169,169,169,0.3)",
            color: "#171717",
          }}
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-4 py-2 border text-sm"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(169,169,169,0.3)",
            color: "#171717",
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>

        {/* NEW: Enrichment Status Filter */}
        <select
          value={embeddingFilter}
          onChange={(e) => setEmbeddingFilter(e.target.value)}
          className="px-4 py-2 border text-sm"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "rgba(169,169,169,0.3)",
            color: "#171717",
          }}
        >
          <option value="">All Enrichment</option>
          <option value="complete">✅ Enriched</option>
          <option value="pending">⏳ Pending</option>
          <option value="processing">🔄 Processing</option>
          <option value="failed">❌ Failed</option>
        </select>
      </div>

      {/* NEW: Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between p-4"
          style={{ backgroundColor: "rgba(0,251,169,0.1)" }}
        >
          <span className="text-sm font-medium" style={{ color: "#171717" }}>
            {selectedIds.size} product{selectedIds.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={async () => {
                setBulkAnalyzing(true);
                const selected = products.filter(
                  (p) =>
                    selectedIds.has(p.id) && (p.image_url || p.thumbnail_url),
                );
                for (const product of selected) {
                  try {
                    await tenantFetch("/api/v1/products/analyze", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        product: {
                          id: product.id,
                          title: product.title,
                          brand: product.brand,
                          category: product.category,
                          imageUrl: product.image_url || product.thumbnail_url,
                        },
                      }),
                    });
                  } catch (err) {
                    console.error("Bulk analysis error:", err);
                  }
                }
                setBulkAnalyzing(false);
                setSelectedIds(new Set());
                // Refresh products
                window.location.reload();
              }}
              disabled={bulkAnalyzing}
              className="btn-primary text-xs py-2 px-4 flex items-center gap-2 disabled:opacity-50"
            >
              {bulkAnalyzing ? (
                <>
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Analyzing
                </>
              ) : (
                "Analyze Selected"
              )}
            </button>
            <button
              onClick={async () => {
                if (
                  !confirm(
                    `Delete ${selectedIds.size} products? This cannot be undone.`,
                  )
                )
                  return;
                let deleted = 0;
                for (const id of selectedIds) {
                  try {
                    const response = await tenantFetch(`/api/products/${id}`, {
                      method: "DELETE",
                    });
                    if (response.ok) deleted++;
                  } catch (e) {
                    console.error("Delete error:", e);
                  }
                }
                setProducts((prev) =>
                  prev.filter((p) => !selectedIds.has(p.id)),
                );
                setPagination((prev) => ({
                  ...prev,
                  total: prev.total - deleted,
                }));
                setSelectedIds(new Set());
              }}
              className="text-xs py-2 px-4 rounded-full border transition-colors hover:bg-penta-coral hover:text-white"
              style={{
                borderColor: "var(--penta-coral)",
                color: "var(--penta-coral)",
              }}
            >
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="btn-secondary text-xs py-2 px-4"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {syncResult && (
        <div
          className="p-4"
          style={{ backgroundColor: "rgba(0,251,169,0.1)", color: "#00FBA9" }}
        >
          {syncResult}
        </div>
      )}
      {error && (
        <div
          className="p-4"
          style={{ backgroundColor: "rgba(255,76,0,0.1)", color: "#FF4C00" }}
        >
          {error}
        </div>
      )}
      {enrichmentError && (
        <div
          className="p-4"
          style={{ backgroundColor: "rgba(255,76,0,0.1)", color: "#FF4C00" }}
        >
          {enrichmentError}
          <button
            onClick={() => setEnrichmentError(null)}
            className="ml-4 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* AI Enrichment Results Modal */}
      {enrichmentResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div
              className="p-6 border-b"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {enrichmentResult.product.thumbnail_url && (
                    <img
                      src={enrichmentResult.product.thumbnail_url}
                      alt=""
                      className="w-16 h-16 rounded object-cover"
                    />
                  )}
                  <div>
                    <h2
                      className="text-xl font-medium"
                      style={{ color: "#171717" }}
                    >
                      AI Analysis: {enrichmentResult.product.title}
                    </h2>
                    <p className="text-sm" style={{ color: "#A9A9A9" }}>
                      Powered by DGX Spark • Vision + Reasoning + Resale Value
                      APIs
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEnrichmentResult(null)}
                  className="text-2xl"
                  style={{ color: "#A9A9A9" }}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Vision Results */}
              <div className="space-y-4">
                <h3
                  className="font-medium flex items-center gap-2"
                  style={{ color: "#171717" }}
                >
                  <span style={{ color: "#00FBA9" }}>👁</span> Vision Analysis
                </h3>
                {enrichmentResult.data.vision ? (
                  <div className="space-y-2 text-sm">
                    {enrichmentResult.data.vision.product_type && (
                      <div>
                        <span style={{ color: "#A9A9A9" }}>Type:</span>{" "}
                        <span style={{ color: "#171717" }}>
                          {enrichmentResult.data.vision.product_type}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.vision.brand && (
                      <div>
                        <span style={{ color: "#A9A9A9" }}>Brand:</span>{" "}
                        <span style={{ color: "#171717" }}>
                          {enrichmentResult.data.vision.brand}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.vision.color && (
                      <div>
                        <span style={{ color: "#A9A9A9" }}>Color:</span>{" "}
                        <span style={{ color: "#171717" }}>
                          {enrichmentResult.data.vision.color}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.vision.material && (
                      <div>
                        <span style={{ color: "#A9A9A9" }}>Material:</span>{" "}
                        <span style={{ color: "#171717" }}>
                          {enrichmentResult.data.vision.material}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.vision.condition && (
                      <div>
                        <span style={{ color: "#A9A9A9" }}>Condition:</span>{" "}
                        <span style={{ color: "#00FBA9" }}>
                          {enrichmentResult.data.vision.condition}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.vision.features &&
                      enrichmentResult.data.vision.features.length > 0 && (
                        <div>
                          <span style={{ color: "#A9A9A9" }}>Features:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {enrichmentResult.data.vision.features.map(
                              (f, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 rounded-full text-xs"
                                  style={{
                                    backgroundColor: "#FAFAFA",
                                    color: "#171717",
                                  }}
                                >
                                  {f}
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    {enrichmentResult.data.vision.description && (
                      <div
                        className="mt-2 p-2 rounded text-xs"
                        style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
                      >
                        {enrichmentResult.data.vision.description}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: "#A9A9A9" }}>
                    No vision data
                  </p>
                )}
              </div>

              {/* Value Results */}
              <div className="space-y-4">
                <h3
                  className="font-medium flex items-center gap-2"
                  style={{ color: "#171717" }}
                >
                  <span style={{ color: "#00FBA9" }}>💰</span> Resale Value
                  Estimation
                </h3>
                {enrichmentResult.data.value ? (
                  <div className="space-y-3">
                    {enrichmentResult.data.value.estimated_value !==
                      undefined && (
                      <div
                        className="text-center p-4"
                        style={{ backgroundColor: "rgba(0,251,169,0.1)" }}
                      >
                        <p className="text-xs" style={{ color: "#A9A9A9" }}>
                          Estimated Value
                        </p>
                        <p
                          className="text-3xl font-light"
                          style={{ color: "#00FBA9" }}
                        >
                          $
                          {enrichmentResult.data.value.estimated_value.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {enrichmentResult.data.value.price_range && (
                      <div className="text-sm">
                        <span style={{ color: "#A9A9A9" }}>Price Range:</span>
                        <span style={{ color: "#171717" }}>
                          {" "}
                          ${enrichmentResult.data.value.price_range.min} - $
                          {enrichmentResult.data.value.price_range.max}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.value.confidence !== undefined && (
                      <div className="text-sm">
                        <span style={{ color: "#A9A9A9" }}>Confidence:</span>
                        <span style={{ color: "#171717" }}>
                          {" "}
                          {Math.round(
                            enrichmentResult.data.value.confidence * 100,
                          )}
                          %
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.value.factors &&
                      enrichmentResult.data.value.factors.length > 0 && (
                        <div>
                          <p
                            className="text-xs mb-1"
                            style={{ color: "#A9A9A9" }}
                          >
                            Factors:
                          </p>
                          <ul className="text-xs space-y-1">
                            {enrichmentResult.data.value.factors.map((f, i) => (
                              <li key={i} style={{ color: "#171717" }}>
                                • {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: "#A9A9A9" }}>
                    No value data
                  </p>
                )}
              </div>

              {/* Intelligence Results */}
              <div className="space-y-4">
                <h3
                  className="font-medium flex items-center gap-2"
                  style={{ color: "#171717" }}
                >
                  <span style={{ color: "#00FBA9" }}>🧠</span> Product
                  Intelligence
                </h3>
                {enrichmentResult.data.intelligence ? (
                  <div className="space-y-2 text-sm">
                    {enrichmentResult.data.intelligence.category && (
                      <div>
                        <span style={{ color: "#A9A9A9" }}>Category:</span>{" "}
                        <span style={{ color: "#171717" }}>
                          {enrichmentResult.data.intelligence.category}
                        </span>
                      </div>
                    )}
                    {enrichmentResult.data.intelligence.attributes &&
                      Object.keys(enrichmentResult.data.intelligence.attributes)
                        .length > 0 && (
                        <div>
                          <span style={{ color: "#A9A9A9" }}>Attributes:</span>
                          <div className="mt-1 space-y-1">
                            {Object.entries(
                              enrichmentResult.data.intelligence.attributes,
                            )
                              .slice(0, 6)
                              .map(([k, v]) => (
                                <div
                                  key={k}
                                  className="flex justify-between text-xs"
                                >
                                  <span style={{ color: "#A9A9A9" }}>{k}:</span>
                                  <span style={{ color: "#171717" }}>{v}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    {enrichmentResult.data.intelligence.keywords &&
                      enrichmentResult.data.intelligence.keywords.length >
                        0 && (
                        <div>
                          <span style={{ color: "#A9A9A9" }}>Keywords:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {enrichmentResult.data.intelligence.keywords
                              .slice(0, 8)
                              .map((k, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 rounded-full text-xs"
                                  style={{
                                    backgroundColor: "rgba(0,251,169,0.15)",
                                    color: "#00FBA9",
                                  }}
                                >
                                  {k}
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: "#A9A9A9" }}>
                    No intelligence data
                  </p>
                )}
              </div>
            </div>

            <div
              className="p-6 border-t flex justify-end gap-3"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <button
                onClick={() => setEnrichmentResult(null)}
                className="px-4 py-2 rounded text-sm"
                style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
              >
                Close
              </button>
              <button
                className="px-4 py-2 rounded text-sm"
                style={{ backgroundColor: "#00FBA9", color: "#171717" }}
              >
                Save to Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead style={{ backgroundColor: "#FAFAFA" }}>
            <tr
              className="border-b"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              {/* Checkbox column for bulk selection */}
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={
                    products.length > 0 && selectedIds.size === products.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set(products.map((p) => p.id)));
                    } else {
                      setSelectedIds(new Set());
                    }
                  }}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: "#00FBA9" }}
                />
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                style={{ color: "#A9A9A9" }}
              >
                Product
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                style={{ color: "#A9A9A9" }}
              >
                SKU
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                style={{ color: "#A9A9A9" }}
              >
                Category
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                style={{ color: "#A9A9A9" }}
              >
                Status
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                style={{ color: "#A9A9A9" }}
              >
                Embedding
              </th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody
            className="divide-y"
            style={{ borderColor: "rgba(169,169,169,0.1)" }}
          >
            {loading ? (
              // Loading state
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-6 py-4" colSpan={7}>
                    <div
                      className="h-8 rounded animate-pulse"
                      style={{ backgroundColor: "#FAFAFA" }}
                    />
                  </td>
                </tr>
              ))
            ) : products.length === 0 ? (
              // Empty state
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div style={{ color: "#A9A9A9" }}>
                    <p className="mb-2">No products yet</p>
                    <p className="text-sm mb-4">
                      Import your catalogue to get started
                    </p>
                    <a
                      href="/products/import"
                      className="btn-primary inline-block"
                    >
                      Import Products
                    </a>
                  </div>
                </td>
              </tr>
            ) : (
              // Product rows
              products.map((product) => (
                <tr
                  key={product.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={(e) => {
                    // Don't navigate if clicking checkbox
                    if ((e.target as HTMLElement).tagName === "INPUT") return;
                    navigate({
                      to: "/products/$id",
                      params: { id: product.id },
                    });
                  }}
                >
                  {/* Checkbox for bulk selection */}
                  <td
                    className="px-4 py-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedIds);
                        if (e.target.checked) {
                          newSelected.add(product.id);
                        } else {
                          newSelected.delete(product.id);
                        }
                        setSelectedIds(newSelected);
                      }}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: "#00FBA9" }}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {product.image_url || product.thumbnail_url ? (
                        <img
                          src={product.image_url || product.thumbnail_url || ""}
                          alt=""
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div
                          className="w-10 h-10 rounded flex items-center justify-center text-xs"
                          style={{
                            backgroundColor: "#FAFAFA",
                            color: "#A9A9A9",
                          }}
                        >
                          No img
                        </div>
                      )}
                      <div>
                        <p className="font-medium" style={{ color: "#171717" }}>
                          {product.title}
                        </p>
                        {product.brand && (
                          <p className="text-xs" style={{ color: "#A9A9A9" }}>
                            {product.brand}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="font-mono text-sm"
                      style={{ color: "#A9A9A9" }}
                    >
                      {product.sku || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm" style={{ color: "#171717" }}>
                      {product.category || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={product.status} />
                  </td>
                  <td className="px-6 py-4">
                    <EmbeddingBadge status={product.embedding_status} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          enrichProduct(product);
                        }}
                        disabled={
                          enrichingId === product.id ||
                          (!product.image_url && !product.thumbnail_url)
                        }
                        className="text-xs font-mono uppercase tracking-wider transition-opacity disabled:opacity-30 hover:opacity-70"
                        style={{ color: "var(--penta-mint)" }}
                        title={
                          !product.image_url && !product.thumbnail_url
                            ? "Needs image for AI"
                            : "Analyze with AI"
                        }
                      >
                        {enrichingId === product.id ? "Analyzing" : "Analyze"}
                      </button>
                      <Link
                        to="/products/$id"
                        params={{ id: product.id }}
                        className="text-xs font-mono uppercase tracking-wider transition-opacity hover:opacity-70"
                        style={{ color: "var(--penta-grey-medium)" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                      </Link>
                      <button
                        onClick={(e) => deleteProduct(product, e)}
                        disabled={deletingId === product.id}
                        className="text-xs font-mono uppercase tracking-wider transition-opacity disabled:opacity-30 hover:opacity-70"
                        style={{ color: "var(--penta-coral)" }}
                        title="Delete product"
                      >
                        {deletingId === product.id ? "..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div
            className="px-6 py-4 flex items-center justify-between border-t"
            style={{ borderColor: "rgba(169,169,169,0.1)" }}
          >
            <p className="text-sm" style={{ color: "#A9A9A9" }}>
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total} products
            </p>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination((p) => ({ ...p, page: p.page - 1 }))
                }
                disabled={pagination.page === 1}
                className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-30"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination((p) => ({ ...p, page: p.page + 1 }))
                }
                disabled={pagination.page === pagination.pages}
                className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <AddProductModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

// Add Product Modal Component
function AddProductModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const response = await tenantFetch("/api/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          brand: brand.trim() || null,
          category: category.trim() || null,
          sku: sku.trim() || null,
          price: price ? parseFloat(price) : null,
          description: description.trim() || null,
          image_url: imageUrl.trim() || null,
          status: "active",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create product");
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-2xl max-w-lg w-full">
        <div
          className="p-6 border-b"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium" style={{ color: "#171717" }}>
              Add New Product
            </h2>
            <button
              onClick={onClose}
              className="text-2xl"
              style={{ color: "#A9A9A9" }}
            >
              &times;
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div
              className="p-3 text-sm"
              style={{
                backgroundColor: "rgba(255,76,0,0.1)",
                color: "#FF4C00",
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label
              className="block text-sm font-medium mb-1"
              style={{ color: "#171717" }}
            >
              Title <span style={{ color: "#FF4C00" }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border"
              style={{ borderColor: "rgba(169,169,169,0.3)" }}
              placeholder="Product title"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "#171717" }}
              >
                Brand
              </label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full px-4 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="Brand name"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "#171717" }}
              >
                Category
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="Category"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "#171717" }}
              >
                SKU
              </label>
              <input
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                className="w-full px-4 py-2 border font-mono"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="SKU-12345"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: "#171717" }}
              >
                Price
              </label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full px-4 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="99.99"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-1"
              style={{ color: "#171717" }}
            >
              Image URL
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-4 py-2 border"
              style={{ borderColor: "rgba(169,169,169,0.3)" }}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-1"
              style={{ color: "#171717" }}
            >
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border"
              style={{ borderColor: "rgba(169,169,169,0.3)" }}
              placeholder="Product description..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full"
              style={{ backgroundColor: "#FAFAFA", color: "#171717" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-full font-medium"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              {saving ? "Creating..." : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string }> = {
    active: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9" },
    draft: { bg: "rgba(169,169,169,0.15)", text: "#A9A9A9" },
    archived: { bg: "rgba(255,76,0,0.15)", text: "#FF4C00" },
  };

  const style = styles[status] || styles.draft;

  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-mono uppercase"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status}
    </span>
  );
}

function EmbeddingBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; label: string }> = {
    complete: { bg: "rgba(0,251,169,0.15)", text: "#00FBA9", label: "Ready" },
    processing: {
      bg: "rgba(57,65,235,0.15)",
      text: "#3941EB",
      label: "Processing",
    },
    pending: {
      bg: "rgba(169,169,169,0.15)",
      text: "#A9A9A9",
      label: "Pending",
    },
    failed: { bg: "rgba(255,76,0,0.15)", text: "#FF4C00", label: "Failed" },
  };

  const style = styles[status] || styles.pending;

  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-mono uppercase"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {style.label}
    </span>
  );
}

// Demo Arc'teryx products for development with realistic retail prices
function getArcteryxDemoProducts(): Product[] {
  return [
    {
      id: "arc-1",
      sku: "ARC-25854-BLK-M",
      title: "Beta SL Jacket",
      description:
        "Lightweight, packable GORE-TEX shell for hiking and trekking. Minimalist design with essential weather protection.",
      brand: "Arc'teryx",
      category: "Shell Jackets",
      price: 600,
      currency: "USD",
      image_url: "/images/products/arcteryx/beta-ar-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/beta-ar-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-01T10:00:00Z",
    },
    {
      id: "arc-2",
      sku: "ARC-24108-PILOT-L",
      title: "Konseal Hybrid Hoody",
      description:
        "Lightweight, breathable hybrid hoody with fleece body and stretch panels. Perfect for climbing and high-output activities.",
      brand: "Arc'teryx",
      category: "Insulated Jackets",
      price: 220,
      currency: "USD",
      image_url: "/images/products/arcteryx/atom-lt-hoody.jpeg",
      thumbnail_url: "/images/products/arcteryx/atom-lt-hoody.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-02T10:00:00Z",
    },
    {
      id: "arc-3",
      sku: "ARC-25681-BLACK-M",
      title: "Alpha SV Jacket",
      description:
        "The pinnacle of alpine protection. Built for severe alpine conditions with the most durable GORE-TEX Pro available. The flagship shell for elite alpinists.",
      brand: "Arc'teryx",
      category: "Shell Jackets",
      price: 1100,
      currency: "USD",
      image_url: "/images/products/arcteryx/alpha-sv-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/alpha-sv-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-03T10:00:00Z",
    },
    {
      id: "arc-4",
      sku: "ARC-17225-BLK-32",
      title: "Beta Pant",
      description:
        "Durable, waterproof GORE-TEX pant for all-around mountain use. Articulated patterning for freedom of movement.",
      brand: "Arc'teryx",
      category: "Pants",
      price: 360,
      currency: "USD",
      image_url: "/images/products/arcteryx/gamma-lt-pants.jpeg",
      thumbnail_url: "/images/products/arcteryx/gamma-lt-pants.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-04T10:00:00Z",
    },
    {
      id: "arc-5",
      sku: "ARC-21773-LABYRINTH-S",
      title: "Cerium SV Vest",
      description:
        "Superlight down vest with exceptional warmth-to-weight ratio. Features 850-fill European goose down for core warmth.",
      brand: "Arc'teryx",
      category: "Insulated Jackets",
      price: 420,
      currency: "USD",
      image_url: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-05T10:00:00Z",
    },
    {
      id: "arc-6",
      sku: "ARC-25445-FLUX-M",
      title: "Emblem Fleece Crew",
      description:
        "Comfortable fleece crew neck pullover with Arc'teryx emblem. Polartec® fleece for everyday warmth.",
      brand: "Arc'teryx",
      category: "Fleece",
      price: 130,
      currency: "USD",
      image_url: "/images/products/arcteryx/proton-fl-hoody.jpeg",
      thumbnail_url: "/images/products/arcteryx/proton-fl-hoody.jpeg",
      status: "active",
      embedding_status: "processing",
      created_at: "2024-12-06T10:00:00Z",
    },
    {
      id: "arc-7",
      sku: "ARC-21776-BLACK-L",
      title: "Kragg Cotton Crew Shirt",
      description:
        "Organic cotton short sleeve crew with Little Bird graphic. Comfortable casual wear with Arc'teryx style.",
      brand: "Arc'teryx",
      category: "Shirts",
      price: 80,
      currency: "USD",
      image_url: "/images/products/arcteryx/zeta-sl-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/zeta-sl-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-07T10:00:00Z",
    },
    {
      id: "arc-8",
      sku: "ARC-12397-BLACK-M",
      title: "Covert Cardigan",
      description:
        "Polartec® fleece cardigan with clean styling for everyday wear. Versatile piece that transitions from trail to town.",
      brand: "Arc'teryx",
      category: "Fleece",
      price: 179,
      currency: "USD",
      image_url: "/images/products/arcteryx/covert-cardigan.jpeg",
      thumbnail_url: "/images/products/arcteryx/covert-cardigan.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-08T10:00:00Z",
    },
    {
      id: "arc-9",
      sku: "ARC-18965-KINGFISHER-S",
      title: "Chunky Ribbed Toque",
      description:
        "Warm, chunky knit toque with ribbed texture. Cozy winter accessory with Arc'teryx quality.",
      brand: "Arc'teryx",
      category: "Accessories",
      price: 80,
      currency: "USD",
      image_url: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
      thumbnail_url: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
      status: "active",
      embedding_status: "pending",
      created_at: "2024-12-09T10:00:00Z",
    },
    {
      id: "arc-10",
      sku: "ARC-26845-BORDEAUX-L",
      title: "Beta LT Jacket",
      description:
        "Lightweight, packable GORE-TEX shell for trekking and hiking. Reduced weight with essential weather protection.",
      brand: "Arc'teryx",
      category: "Shell Jackets",
      price: 550,
      currency: "USD",
      image_url: "/images/products/arcteryx/beta-lt-jacket.jpeg",
      thumbnail_url: "/images/products/arcteryx/beta-lt-jacket.jpeg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-10T10:00:00Z",
    },
  ];
}

function getLululemonDemoProducts(): Product[] {
  return [
    {
      id: "lulu-1",
      sku: "LW5CTIS-BLK-6",
      title: 'Align High-Rise Pant 25"',
      description:
        "Buttery-soft, weightless Nulu™ fabric. Our most popular legging for yoga and everyday comfort.",
      brand: "lululemon",
      category: "Leggings",
      price: 98,
      currency: "USD",
      image_url: "/images/lululemon/align-pant.jpg",
      thumbnail_url: "/images/lululemon/align-pant.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-01T10:00:00Z",
    },
    {
      id: "lulu-2",
      sku: "LW5DEDS-NAVY-8",
      title: 'Wunder Train High-Rise Tight 25"',
      description:
        "Everlux™ fabric dries fast and wicks sweat. Designed for training and sweaty workouts.",
      brand: "lululemon",
      category: "Leggings",
      price: 98,
      currency: "USD",
      image_url: "/images/lululemon/wunder-train.jpg",
      thumbnail_url: "/images/lululemon/wunder-train.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-02T10:00:00Z",
    },
    {
      id: "lulu-3",
      sku: "LW3EMES-WHT-S",
      title: "Swiftly Tech Racerback Tank 2.0",
      description:
        "Silverescent™ technology inhibits odor. Seamless construction for chafe-free running.",
      brand: "lululemon",
      category: "Tops",
      price: 68,
      currency: "USD",
      image_url: "/product-images/lulu-align.png",
      thumbnail_url: "/product-images/lulu-align.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-03T10:00:00Z",
    },
    {
      id: "lulu-4",
      sku: "LW4AWNS-DKOL-M",
      title: "Define Jacket",
      description:
        "Luon® fabric is sweat-wicking and four-way stretch. Thumbholes keep sleeves in place.",
      brand: "lululemon",
      category: "Jackets",
      price: 118,
      currency: "USD",
      image_url: "/product-images/lulu-define.png",
      thumbnail_url: "/product-images/lulu-define.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-04T10:00:00Z",
    },
    {
      id: "lulu-5",
      sku: "LW3BXNS-BLK-6",
      title: "Energy Bra High Support",
      description:
        "High support for B-D cups. Luxtreme™ fabric is sweat-wicking and four-way stretch.",
      brand: "lululemon",
      category: "Bras",
      price: 68,
      currency: "USD",
      image_url: "/images/lululemon/offer-bra.jpg",
      thumbnail_url: "/images/lululemon/offer-bra.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-05T10:00:00Z",
    },
    {
      id: "lulu-6",
      sku: "LW3DLRS-CHER-M",
      title: "Scuba Oversized Full Zip Hoodie",
      description:
        "Cotton fleece fabric is soft and warm. Oversized fit for cozy comfort.",
      brand: "lululemon",
      category: "Hoodies",
      price: 128,
      currency: "USD",
      image_url: "/images/lululemon/scuba-hoodie.jpg",
      thumbnail_url: "/images/lululemon/scuba-hoodie.jpg",
      status: "active",
      embedding_status: "processing",
      created_at: "2024-12-06T10:00:00Z",
    },
    {
      id: "lulu-7",
      sku: "LW5DERS-SAGE-4",
      title: 'Fast and Free High-Rise Tight 25"',
      description:
        "Nulux™ is quick-drying and weightless. Side drop-in pockets fit your phone.",
      brand: "lululemon",
      category: "Leggings",
      price: 128,
      currency: "USD",
      image_url: "/images/lululemon/like-new-items.jpg",
      thumbnail_url: "/images/lululemon/like-new-items.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-07T10:00:00Z",
    },
    {
      id: "lulu-8",
      sku: "LW7ANRS-BLK-M",
      title: 'Hotty Hot High-Rise Short 4"',
      description:
        "Swift™ fabric is lightweight and durable. Built-in liner for coverage.",
      brand: "lululemon",
      category: "Shorts",
      price: 68,
      currency: "USD",
      image_url: "/images/lululemon/clean-gear.jpg",
      thumbnail_url: "/images/lululemon/clean-gear.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-08T10:00:00Z",
    },
  ];
}

function getNespressoDemoProducts(): Product[] {
  return [
    {
      id: "nes-1",
      sku: "NES-VER-INT-10",
      title: "Vertuo Intenso",
      description:
        "Rich and bold coffee with notes of wood and leather. Intensity 9/13.",
      brand: "Nespresso",
      category: "Vertuo Coffee",
      price: 11.5,
      currency: "USD",
      image_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-intenso.jpg",
      thumbnail_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-intenso.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-01T10:00:00Z",
    },
    {
      id: "nes-2",
      sku: "NES-OL-RIS-10",
      title: "Original Ristretto",
      description:
        "Powerful and contrasted blend with fine acidity. Intensity 10/13.",
      brand: "Nespresso",
      category: "Original Coffee",
      price: 8.5,
      currency: "USD",
      image_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/ristretto.jpg",
      thumbnail_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/ristretto.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-02T10:00:00Z",
    },
    {
      id: "nes-3",
      sku: "NES-VER-MEL-10",
      title: "Vertuo Melozio",
      description:
        "Smooth and balanced coffee with biscuity notes. Intensity 6/13.",
      brand: "Nespresso",
      category: "Vertuo Coffee",
      price: 11.5,
      currency: "USD",
      image_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-melozio.jpg",
      thumbnail_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-melozio.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-03T10:00:00Z",
    },
    {
      id: "nes-4",
      sku: "NES-VER-STMZ-10",
      title: "Vertuo Stormio",
      description:
        "Full-bodied and woody coffee with spicy notes. Intensity 8/13.",
      brand: "Nespresso",
      category: "Vertuo Coffee",
      price: 11.5,
      currency: "USD",
      image_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-stormio.jpg",
      thumbnail_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-stormio.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-04T10:00:00Z",
    },
    {
      id: "nes-5",
      sku: "NES-OL-VOL-10",
      title: "Original Volluto",
      description:
        "Sweet and light coffee with biscuity notes. Intensity 4/13.",
      brand: "Nespresso",
      category: "Original Coffee",
      price: 8.5,
      currency: "USD",
      image_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/volluto.jpg",
      thumbnail_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/volluto.jpg",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-12-05T10:00:00Z",
    },
    {
      id: "nes-6",
      sku: "NES-VER-ICE-10",
      title: "Vertuo Ice Forte",
      description:
        "Designed for iced coffee. Bold and intense over ice. Intensity 8/13.",
      brand: "Nespresso",
      category: "Vertuo Ice Coffee",
      price: 12.0,
      currency: "USD",
      image_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-ice-forte.jpg",
      thumbnail_url:
        "https://www.nespresso.com/shared_res/mos/free_html/us/vertuo-ice-forte.jpg",
      status: "active",
      embedding_status: "processing",
      created_at: "2024-12-06T10:00:00Z",
    },
  ];
}

function getLegoDemoProducts(): Product[] {
  return [
    {
      id: "lego-1",
      sku: "42056",
      title: "Technic Porsche 911 GT3 RS",
      description:
        "Highly detailed 1:8 scale model of the iconic Porsche 911 GT3 RS with working gearbox, steering, and suspension.",
      brand: "LEGO Technic",
      category: "Technic",
      price: 299.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt3e6d0c0c0a1a0a0a/42056.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt3e6d0c0c0a1a0a0a/42056.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-01-15T10:00:00Z",
    },
    {
      id: "lego-2",
      sku: "75192",
      title: "Star Wars Millennium Falcon",
      description:
        "Ultimate Collector Series Millennium Falcon with 7,541 pieces. The largest LEGO set ever produced.",
      brand: "LEGO Star Wars",
      category: "Star Wars",
      price: 849.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt1c5c5c5c5c5c5c5c/75192.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt1c5c5c5c5c5c5c5c/75192.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-01-20T10:00:00Z",
    },
    {
      id: "lego-3",
      sku: "10294",
      title: "Titanic",
      description:
        "1:200 scale replica of the legendary ocean liner with detailed interior sections.",
      brand: "LEGO Icons",
      category: "Icons",
      price: 679.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt2d2d2d2d2d2d2d2d/10294.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt2d2d2d2d2d2d2d2d/10294.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-02-01T10:00:00Z",
    },
    {
      id: "lego-4",
      sku: "21054",
      title: "Architecture The White House",
      description:
        "Detailed recreation of the iconic presidential residence in Washington D.C.",
      brand: "LEGO Architecture",
      category: "Architecture",
      price: 99.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt4e4e4e4e4e4e4e4e/21054.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt4e4e4e4e4e4e4e4e/21054.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-02-10T10:00:00Z",
    },
    {
      id: "lego-5",
      sku: "10281",
      title: "Botanical Collection Bonsai Tree",
      description:
        "Build and display this beautiful bonsai tree with interchangeable green leaves or pink cherry blossoms.",
      brand: "LEGO Botanical",
      category: "Botanical",
      price: 49.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt5f5f5f5f5f5f5f5f/10281.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt5f5f5f5f5f5f5f5f/10281.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-02-15T10:00:00Z",
    },
    {
      id: "lego-6",
      sku: "76419",
      title: "Harry Potter Hogwarts Castle and Grounds",
      description:
        "Massive recreation of Hogwarts featuring the castle, Hagrid's Hut, and Whomping Willow.",
      brand: "LEGO Harry Potter",
      category: "Harry Potter",
      price: 169.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt6g6g6g6g6g6g6g6g/76419.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt6g6g6g6g6g6g6g6g/76419.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-03-01T10:00:00Z",
    },
    {
      id: "lego-7",
      sku: "10330",
      title: "McLaren MP4/4 & Ayrton Senna",
      description:
        "Celebrate the legendary 1988 Formula 1 season with this detailed replica of the iconic McLaren.",
      brand: "LEGO Icons",
      category: "Icons",
      price: 79.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt7h7h7h7h7h7h7h7h/10330.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt7h7h7h7h7h7h7h7h/10330.png",
      status: "active",
      embedding_status: "complete",
      created_at: "2024-03-10T10:00:00Z",
    },
    {
      id: "lego-8",
      sku: "71043",
      title: "Hogwarts Castle",
      description:
        "Micro-scale recreation of the iconic Hogwarts School with over 6,000 pieces.",
      brand: "LEGO Harry Potter",
      category: "Harry Potter",
      price: 469.99,
      currency: "USD",
      image_url:
        "https://www.lego.com/cdn/cs/set/assets/blt8i8i8i8i8i8i8i8i/71043.png",
      thumbnail_url:
        "https://www.lego.com/cdn/cs/set/assets/blt8i8i8i8i8i8i8i8i/71043.png",
      status: "active",
      embedding_status: "pending",
      created_at: "2024-03-15T10:00:00Z",
    },
  ];
}

function getNikeDemoProducts(): Product[] {
  const nikeImagePath = "/assets/images/products/nike";
  return [
    {
      id: "nike-1",
      sku: "CW2288-111",
      title: "Air Force 1 '07",
      description:
        "The radiance lives on in the Nike Air Force 1 '07. Crisp leather and bold colors give you iconic style.",
      brand: "Nike",
      category: "Lifestyle",
      price: 110.0,
      currency: "USD",
      image_url: `${nikeImagePath}/NIKE+AIR+FORCE+1+_07.png`,
      thumbnail_url: `${nikeImagePath}/NIKE+AIR+FORCE+1+_07.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-01-01T10:00:00Z",
    },
    {
      id: "nike-2",
      sku: "DQ3085-104",
      title: "Air Max Plus",
      description:
        "The Nike Air Max Plus features a gradient upper and visible Tuned Air cushioning for all-day comfort.",
      brand: "Nike",
      category: "Lifestyle",
      price: 175.0,
      currency: "USD",
      image_url: `${nikeImagePath}/NIKE+AIR+MAX+PLUS.png`,
      thumbnail_url: `${nikeImagePath}/NIKE+AIR+MAX+PLUS.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-01-05T10:00:00Z",
    },
    {
      id: "nike-3",
      sku: "FD2720-001",
      title: "Air Max 95 Big Bubble",
      description:
        "The Air Max 95 Big Bubble celebrates visible Air with a special edition colorway and premium materials.",
      brand: "Nike",
      category: "Lifestyle",
      price: 185.0,
      currency: "USD",
      image_url: `${nikeImagePath}/AIR+MAX+95+BIG+BUBBLE+ZIP+SP.png`,
      thumbnail_url: `${nikeImagePath}/AIR+MAX+95+BIG+BUBBLE+ZIP+SP.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-01-10T10:00:00Z",
    },
    {
      id: "nike-4",
      sku: "AV3733-003",
      title: "Shox TL",
      description:
        "Nike Shox TL brings mechanical cushioning with responsive columns for a smooth ride.",
      brand: "Nike",
      category: "Lifestyle",
      price: 160.0,
      currency: "USD",
      image_url: `${nikeImagePath}/NIKE+SHOX+TL.png`,
      thumbnail_url: `${nikeImagePath}/NIKE+SHOX+TL.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-02-01T10:00:00Z",
    },
    {
      id: "nike-5",
      sku: "CD5463-100",
      title: "Court Vision Mid Winter",
      description:
        "Basketball-inspired style meets winter-ready comfort with premium materials and a mid-cut silhouette.",
      brand: "Nike",
      category: "Lifestyle",
      price: 90.0,
      currency: "USD",
      image_url: `${nikeImagePath}/NIKE+COURT+VISION+MID+WNTR.png`,
      thumbnail_url: `${nikeImagePath}/NIKE+COURT+VISION+MID+WNTR.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-02-15T10:00:00Z",
    },
    {
      id: "nike-6",
      sku: "FB2065-001",
      title: "Tech Fleece Full-Zip Hoodie",
      description:
        "Lightweight warmth and modern style. Nike Tech Fleece delivers premium comfort without the bulk.",
      brand: "Nike",
      category: "Apparel",
      price: 130.0,
      currency: "USD",
      image_url: `${nikeImagePath}/M+NK+TF+HD+FZ.png`,
      thumbnail_url: `${nikeImagePath}/M+NK+TF+HD+FZ.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-03-01T10:00:00Z",
    },
    {
      id: "nike-7",
      sku: "BA5954-010",
      title: "Heritage Backpack",
      description:
        "Classic Nike style meets everyday functionality. Multiple compartments keep your gear organized.",
      brand: "Nike",
      category: "Accessories",
      price: 40.0,
      currency: "USD",
      image_url: `${nikeImagePath}/NK+HERITAGE+BKPK.png`,
      thumbnail_url: `${nikeImagePath}/NK+HERITAGE+BKPK.png`,
      status: "active",
      embedding_status: "complete",
      created_at: "2024-03-15T10:00:00Z",
    },
    {
      id: "nike-8",
      sku: "BA5957-010",
      title: "Brasilia Duffel Bag (41L)",
      description:
        "Training essential with durable construction. Separate shoe compartment keeps gear fresh.",
      brand: "Nike",
      category: "Accessories",
      price: 45.0,
      currency: "USD",
      image_url: `${nikeImagePath}/NK+BRSLA+S+DUFF+-+9.5+(41L).png`,
      thumbnail_url: `${nikeImagePath}/NK+BRSLA+S+DUFF+-+9.5+(41L).png`,
      status: "active",
      embedding_status: "pending",
      created_at: "2024-04-01T10:00:00Z",
    },
  ];
}

function getTenantDemoProducts(tenantId: string): Product[] {
  switch (tenantId) {
    case "arcteryx":
      return getArcteryxDemoProducts();
    case "lululemon":
      return getLululemonDemoProducts();
    case "nespresso":
      return getNespressoDemoProducts();
    case "lego":
      return getLegoDemoProducts();
    case "nike":
      return getNikeDemoProducts();
    default:
      // Return empty array for unknown tenants - don't show other brand's products
      return [];
  }
}
